import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import {
  type Point,
  type Rect,
  drawableRectOfShape,
  rotationPivotOfShape,
} from '@/lib/shapes/shape-coordinates'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'

/** Narrows to a shape-backed frame, so `frame.shape` is non-optional in the branch. */
export function isShapeBackedFrame(
  frame: LinkableTextFrame
): frame is LinkableTextFrame & { shape: ShapeData } {
  return frame.shape != null
}

/**
 * True when a shape is acting as a text frame and may join a chain.
 *
 * Chain eligibility is exactly "is this shape a text frame" — presence of `paragraphs`,
 * not content, so linking *into* an empty converted frame (the common case) works. That predicate
 * is shared with the canvas double-click router, which needs the identical answer, so it lives in
 * `isShapeTextFrame` (`lib/shapes/shape-text.ts`) and is delegated to here rather than duplicated —
 * the two drifting apart is what WMPCM-8969 was.
 */
export function isLinkableShapeTextFrame(shape: ShapeData): boolean {
  return isShapeTextFrame(shape)
}

export function projectShapeToLinkable(shape: ShapeData): LinkableTextFrame {
  return {
    id: shape.id,
    uid: shape.uid,
    x: shape.x,
    y: shape.y,
    width: shape.width,
    height: shape.height,
    rotation: shape.rotation,
    locked: shape.locked,
    frozen: shape.frozen,
    hidden: shape.hidden,
    parentGroupId: shape.parentGroupId,
    paragraphs: shape.paragraphs ?? [],
    shape,
  }
}

/**
 * The rect and rotation pivot to render text-linking chrome (ports, connectors, hover outline)
 * against. A shape-backed frame's ports must sit on its *visual* box — the tight box around the
 * drawn geometry — not the geometric generator rect (`x`/`y`/`width`/`height`), which for an inset
 * shape (triangle, star, custom path) extends past the shape's own edges, leaving the "+" floating
 * off the visible border. Rotation still pivots on the geometric rect's center
 * (`rotationPivotOfShape`) — the same point the shape itself rotates about — never the visual rect's
 * own center, which sits off-center from the geometric one for an asymmetric inset (see
 * `shape-coordinates.ts`).
 *
 * A Text-tool frame (no `shape`) is always a plain rectangle, so its visual and geometric rects
 * coincide and this just returns its own bounds.
 */
export function linkingChromeRectOfFrame(frame: LinkableTextFrame): { rect: Rect; pivot: Point } {
  if (frame.shape) {
    return { rect: drawableRectOfShape(frame.shape), pivot: rotationPivotOfShape(frame.shape) }
  }

  return {
    rect: { x: frame.x, y: frame.y, width: frame.width, height: frame.height },
    pivot: { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 },
  }
}

/**
 * The full population of linkable frames on the spread — the single source the linking overlay,
 * the Text-tool hover hit test, and chain reflow all draw from.
 */
export function collectLinkableFrames(shapes: ShapeData[]): LinkableTextFrame[] {
  const frames: LinkableTextFrame[] = []

  for (const shape of shapes) {
    if (isLinkableShapeTextFrame(shape)) frames.push(projectShapeToLinkable(shape))
  }

  return frames
}
